import { navigateToAndResetPageAndSearch } from 'front-end/src/pages/analytics/helpers'
import React, {
  ChangeEvent,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import { truncate } from 'lodash'
import { Column } from 'react-table'
import classNames from 'classnames'
import { Box, Flex, Text, Checkbox } from 'front-end/src/components'
import { Trans, t } from '@lingui/macro'

import {
  AnalyticsCertificationDetailFilter,
  CertificationDetailOverviewOrderColumn,
  CertificationEnrolmentStatusEnum,
  SqlOrderDirectionEnum,
  WorkbookStatusEnum
} from 'front-end/src/graphql/private/PrivateSchemaTypes'
import { formatDateShort } from 'front-end/src/utils/dateHelpers'

import { linkBuilder } from 'shared/linkBuilder'
import { getPagesCount } from '../utils'
import { DownloadCertificationDetailCSV } from './DownloadCertificationDetailCSV'
import Tooltip from '../../../components/common/tooltip/Tooltip'
import IconShare from '../../../components/common/icon/IconShare'
import IconClock from '../../../components/common/icon/IconClock'
import {
  useCertificationBatchArchiveEnrolmentsLazyQuery,
  useCertificationDetailOverviewTableQuery
} from './CertificationDetailOverviewTable.codegen'
import dayjs from 'shared/dayjs'
import { AnalyticsContext } from '../AnalyticsContext'
import {
  StatusArchivedGrey,
  StatusProgressCompleted,
  StatusProgressDue,
  StatusProgressInProgress,
  StatusProgressNotStarted
} from 'front-end/src/components/common/status/Status'
import TableExtendedOld, {
  DEFAULT_TR_PROPS
} from 'front-end/src/components/common/table-extended-old/TableExtendedOld'
import CertificationDetailOverviewTableBulkAction from 'front-end/src/pages/analytics/certification-detail/CertificationDetailOverviewTableBulkAction'

interface IProps {
  certificationId?: number
  userId?: number
}

const CertificationDetailOverviewTable: FC<IProps> = ({
  certificationId,
  userId
}: IProps) => {
  const { filterAsQueryVariables, filter, changeFilter } =
    useContext(AnalyticsContext)
  const [overviewTableFilters, setOverviewTableFilters] = useState<
    NonNullable<AnalyticsCertificationDetailFilter>
  >({
    limit: 20,
    orderColumn: CertificationDetailOverviewOrderColumn.displayName,
    orderDirection: SqlOrderDirectionEnum.ASC,
    certificationId: certificationId,
    userId: userId,
    campaignIds: null
  })

  const overviewTableQuery = {
    ...overviewTableFilters,
    page: filter.page,
    search: filter.search
  }

  React.useEffect(() => {
    setOverviewTableFilters((f) => ({ ...f, campaignIds: filter.campaignIds }))
  }, [filter.campaignIds])

  const { data, loading, refetch } = useCertificationDetailOverviewTableQuery({
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      mainAnalyticsFilter: filterAsQueryVariables,
      certificationsOverviewFilter: overviewTableQuery
    }
  })
  const [setEnrolmentsArchived, { loading: isLoadingBatchArchived }] =
    useCertificationBatchArchiveEnrolmentsLazyQuery({
      onCompleted: refetch
    })

  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([])

  const handleSelectAll = (event: ChangeEvent<HTMLInputElement>) => {
    if (
      !data ||
      !data?.analytics.certifications?.certificationEnrolments?.items
    )
      return

    const isChecked = event.currentTarget.checked
    const idsList: number[] = []
    const currentData =
      data?.analytics.certifications?.certificationEnrolments?.items

    for (let row of currentData) {
      if (isChecked && selectedUserIds.includes(row.id)) continue

      idsList.push(row.id)
    }

    if (isChecked) {
      setSelectedUserIds([...selectedUserIds, ...idsList])
    } else {
      setSelectedUserIds(selectedUserIds.filter((id) => !idsList.includes(id)))
    }
  }

  const selectUser = React.useCallback(
    (newId: number) => {
      let update: number[] = []

      if (selectedUserIds.includes(newId)) {
        update = selectedUserIds.filter((id) => id !== newId)
      } else {
        update = [...selectedUserIds, newId]
      }

      setSelectedUserIds(update)
    },
    [selectedUserIds]
  )

  const isPageSelected = useMemo(() => {
    const currentData =
      data?.analytics.certifications?.certificationEnrolments?.items

    if (!currentData || !currentData.length) return false

    return currentData.every((item) => selectedUserIds.includes(item.id))
  }, [
    selectedUserIds,
    data?.analytics.certifications?.certificationEnrolments?.items
  ])

  const limit = overviewTableQuery.limit || 20

  const archiveSelected = async (enrolmentIds: number[]) => {
    if (!certificationId) return
    await setEnrolmentsArchived({
      variables: {
        certificationId: certificationId,
        enrolmentIds
      }
    })
  }

  const tableColumns: Array<Column<any>> = [
    {
      Header: t`Started`,
      maxWidth: 140,
      accessor: 'started_at',
      id: CertificationDetailOverviewOrderColumn.startedAt,
      Cell: (row) => formatDateShort(row.value)
    },
    {
      Header: t`Completed`,
      maxWidth: 140,
      accessor: 'completed_at',
      id: CertificationDetailOverviewOrderColumn.completedAt,
      Cell: (row) => <span className="green">{formatDateShort(row.value)}</span>
    },
    {
      Header: t`Expires`,
      maxWidth: 140,
      accessor: 'expires_at',
      id: CertificationDetailOverviewOrderColumn.expiresAt,
      Cell: (row) => {
        if (dayjs(row.value).isAfter(dayjs())) {
          return formatDateShort(row.value)
        } else {
          return <span className="red">{formatDateShort(row.value)}</span>
        }
      }
    },
    {
      Header: t`Progress`,
      maxWidth: 140,
      accessor: 'progress',
      id: CertificationDetailOverviewOrderColumn.progress,
      Cell: (row) => <span className="grey">{row.value}%</span>
    },
    {
      Header: t`Status`,
      sortable: false,
      accessor: 'status',
      maxWidth: 200,
      Cell: (row) => {
        return <div className="result">{getStatus(row.original.status)}</div>
      }
    }
  ]

  if (certificationId) {
    tableColumns.unshift(
      {
        Header: () => {
          if (!data?.analytics.certifications?.certificationEnrolments?.items)
            return null

          return (
            <Checkbox
              aria-label={t`Select all`}
              isChecked={isPageSelected}
              onChange={handleSelectAll}
            />
          )
        },
        headerStyle: {
          paddingLeft: 8,
          paddingRight: 8
        },
        accessor: 'checked',
        id: 'checked',
        width: 40,
        sortable: false,
        Cell: (row) => {
          const userId = row.original.id

          return (
            <Checkbox
              aria-label={t`Selected`}
              isChecked={selectedUserIds.includes(userId)}
              onChange={() => selectUser(userId)}
              _before={{
                content: '""',
                position: 'absolute',
                width: '40px',
                height: '40px',
                top: '-12px',
                left: '-12px',
                zIndex: 1
              }}
            />
          )
        }
      },
      {
        Header: t`User`,
        accessor: 'user.display_name',
        id: CertificationDetailOverviewOrderColumn.displayName,
        Cell: (row) => {
          const hasCompleted = !!row.original.completed_at
          const isOverdue =
            !hasCompleted &&
            dayjs().isSameOrAfter(dayjs(row.original.target_date))

          let tooltip: ReactNode = null

          if (row.original.shared_at) {
            const tooltipText = (
              <>
                <Trans>Shared on</Trans>{' '}
                {formatDateShort(row.original.shared_at)}
                {row.original.target_date && (
                  <>
                    <br />
                    <strong>
                      <Trans>
                        Due on {formatDateShort(row.original.target_date)}
                      </Trans>
                    </strong>
                  </>
                )}
              </>
            )

            tooltip = (
              <div className="icons">
                <Tooltip text={tooltipText}>
                  <IconShare />
                </Tooltip>
                {isOverdue && <IconClock />}
              </div>
            )
          }

          return (
            <Flex
              flexGrow={1}
              justifyContent="space-between"
              alignItems="center"
            >
              <Box>
                <Text
                  as="strong"
                  className="dark"
                  noOfLines={2}
                  wordBreak="break-all"
                  whiteSpace="normal"
                >
                  {truncate(row.value, { length: 120 })}
                </Text>
              </Box>
              <Box>{tooltip}</Box>
            </Flex>
          )
        }
      }
    )
  }

  if (userId) {
    tableColumns.unshift({
      Header: t`Certification`,
      accessor: 'certification.name',
      id: CertificationDetailOverviewOrderColumn.certificationName,
      Cell: (row) => {
        return (
          <Box whiteSpace="normal">
            <strong className="dark">
              {truncate(row.value, { length: 120 })}
              {row.original.certification.deleted_at && (
                <StatusArchivedGrey mx="8px" />
              )}
            </strong>
          </Box>
        )
      }
    })
  }

  let pagesCount = 1
  if (data && data.analytics) {
    pagesCount = getPagesCount(
      data.analytics.certifications?.certificationEnrolments?.count || 0,
      limit
    )
  }

  useEffect(() => {
    setSelectedUserIds([])
  }, [
    filterAsQueryVariables,
    overviewTableQuery.orderColumn,
    overviewTableQuery.orderDirection,
    overviewTableQuery.search,
    overviewTableQuery.certificationId,
    overviewTableQuery.userId
  ])

  return (
    <div>
      <TableExtendedOld
        loading={loading}
        manual
        data={data?.analytics.certifications?.certificationEnrolments?.items}
        tableHeader={userId ? t`Certificates` : t`User records`}
        columns={tableColumns}
        className="-highlight AnalyticsTable AnalyticsTable__certificationDetail"
        defaultPageSize={limit}
        showPageSizeOptions={false}
        showSearchFilter
        resizable={false}
        minRows={1}
        pages={pagesCount}
        filterFields={['displayName']}
        filterContentBefore={
          certificationId ? (
            <CertificationDetailOverviewTableBulkAction
              selectedIds={selectedUserIds}
              isLoading={isLoadingBatchArchived}
              archiveEnrolments={archiveSelected}
            />
          ) : undefined
        }
        getTrProps={(_state, row) => ({
          onClick: (event) => {
            if (
              event.target.closest(
                "span[class^='chakra-checkbox'], input[class^='chakra-checkbox'], label[class^='chakra-checkbox']"
              ) === null
            ) {
              navigateToAndResetPageAndSearch(
                linkBuilder.analyticsCertificationEnrolmentDetail(
                  row.original.certification.id,
                  row.original.id
                ),
                changeFilter
              )
            }
          },
          className: classNames({
            archived: row && row.original.deleted_at,
            clickable: true
          }),
          ...DEFAULT_TR_PROPS
        })}
        paginationContentAfter={
          data &&
          data.analytics?.certifications && (
            <DownloadCertificationDetailCSV
              certificationsEnrolmentsOverviewFilter={overviewTableQuery}
              certificationsEnrolmentsCount={
                data.analytics.certifications?.certificationEnrolments?.count ||
                0
              }
            />
          )
        }
        defaultSortDesc
        defaultSorted={[
          {
            id: overviewTableQuery.orderColumn as string,
            desc:
              overviewTableQuery.orderDirection === SqlOrderDirectionEnum.DESC
          }
        ]}
        onSortedChange={(newSorted) => {
          setOverviewTableFilters(
            (prevState: NonNullable<AnalyticsCertificationDetailFilter>) => {
              const descendingOrder = newSorted[0] ? newSorted[0].desc : false

              return {
                ...prevState,
                orderColumn: newSorted[0]
                  ? (newSorted[0].id as CertificationDetailOverviewOrderColumn)
                  : CertificationDetailOverviewOrderColumn.displayName,
                orderDirection: descendingOrder
                  ? SqlOrderDirectionEnum.DESC
                  : SqlOrderDirectionEnum.ASC
              }
            }
          )
        }}
        onPageChange={(newPage) => {
          changeFilter({ page: newPage + 1 })
        }}
        defaultSearch={overviewTableQuery.search}
        page={overviewTableQuery.page - 1}
        onSearchChange={(searchVal) => {
          changeFilter({ search: searchVal })
        }}
      />
    </div>
  )
}

const getStatus = (status: CertificationEnrolmentStatusEnum): ReactNode => {
  switch (status) {
    case CertificationEnrolmentStatusEnum.COMPLETED:
      return <StatusProgressCompleted />
    case CertificationEnrolmentStatusEnum.IN_PROGRESS:
      return <StatusProgressInProgress />
    case CertificationEnrolmentStatusEnum.NOT_STARTED:
      return <StatusProgressNotStarted />
    case CertificationEnrolmentStatusEnum.OVERDUE:
      return <StatusProgressDue status={WorkbookStatusEnum.OVERDUE} />
  }
}

export default CertificationDetailOverviewTable
